import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Select, Store } from "@ngxs/store";

import { Observable } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";
import moment from "moment";

import { environment } from "../../../environments/environment";
import {
  GenerateReportAction,
  LoadLastLogsAction,
  LoadVehicleLogsAction,
  ResetLogStateAction,
} from "../../ngxs/log/log.actions";
import { LogGetterState } from "../../ngxs/log";
import { IRequestsNestedState } from "../../ngxs/interfaces/requests.interface";
import {
  isAccelerationIncident,
  isHardBrakingIncident,
  isIridiumIncident,
  isOverRPMIncident,
  isSpeedingIncident,
} from "../../shared/utils/check-incident";
import { GenerateReportRequest } from "../models/generate-report-request.model";
import { TripReport } from "../models/trip-report.model";
import { Trip } from "../models/trip.model";
import { LogModel } from "../models";

import { UserService } from "./user.service";

@Injectable({
  providedIn: "root",
})
export class LogService {
  @Select(LogGetterState.getLastLogs)
  lastLogs$: Observable<LogModel[]>;

  @Select(LogGetterState.getColoredLiveLogs)
  coloredLiveLogs$: Observable<{
    logs: LogModel[];
    colors: { [key: number]: string };
  }>;

  @Select(LogGetterState.getColoredLiveLogsGroupedByVehicle)
  coloredGroupedLiveLogs$: Observable<{
    groupedLogs: LogModel[][];
    colors: { [key: number]: string };
  }>;

  @Select(LogGetterState.getLiveLogsGroupedByVehicle)
  lastLogsGrouped$: Observable<LogModel[][]>;

  @Select(LogGetterState.getColors)
  colors$: Observable<{ [key: number]: string }>;

  @Select(LogGetterState.getReport)
  report$: Observable<Array<{ tripDate: Date; items: TripReport[] }>>;

  @Select(LogGetterState.getGenerateReportRequestState)
  generateReportRequest$: Observable<IRequestsNestedState>;

  @Select(LogGetterState.getLastLogsGetRequestState)
  getLastLogsGetRequestState$: Observable<IRequestsNestedState>;

  waypointSymbolPath =
    "M24.291,14.276L14.705,4.69c-0.878-0.878-2.317-0.878-3.195,0l-0.8,0.8c-0.878,0.877-0.878,2.316,0,3.194  L18.024,16l-7.315,7.315c-0.878,0.878-0.878,2.317,0,3.194l0.8,0.8c0.878,0.879,2.317,0.879,3.195,0l9.586-9.587  c0.472-0.471,0.682-1.103,0.647-1.723C24.973,15.38,24.763,14.748,24.291,14.276z";

  constructor(
    private store: Store,
    private httpClient: HttpClient,
    private userService: UserService
  ) {}

  loadLastLogs() {
    this.store.dispatch(new LoadLastLogsAction());
  }

  loadSnapToRoadDataRequest(data): Observable<any> {
    return this.httpClient.get("https://roads.googleapis.com/v1/snapToRoads", {
      params: {
        interpolate: "true",
        key: environment.googleMapKey,
        path: data,
      },
    });
  }

  loadBingSnapToRoadDataRequest(
    points: { latitude: number; longitude: number }[]
  ): Observable<any> {
    const bingKey = environment.bingMapKey;
    return this.userService.hasCurrentUserPremiumAccess$.pipe(
      // allow only premium users to use snap to road
      filter((isPremium) => isPremium),
      switchMap(() =>
        this.httpClient.post(
          `https://dev.virtualearth.net/REST/v1/Routes/SnapToRoad?key=${bingKey}`,
          {
            points,
            interpolate: true,
            includeSpeedLimit: false,
            includeTruckSpeedLimit: false,
            speedUnit: "KPH",
            travelMode: "driving",
          },
          {
            headers: {
              origin: environment.clientUrl,
            },
          }
        )
      ),
      take(1)
    );
  }

  // TODO: remove if not necessary
  // loadBingSnapToRoadDataAsyncRequest(points: {latitude: number, longitude: number}[]): Observable<any> {
  //   const bingKey = environment.bingMapKey;
  //   return this.httpClient.post(`https://dev.virtualearth.net/REST/v1/Routes/SnapToRoadAsync?key=${bingKey}`, {
  //     points,
  //     interpolate: true,
  //   });
  // }
  //
  // snapToRoadResultRequest(url) {
  //   return this.httpClient.get(url + `&DownloadResult=true`);
  // }

  loadVehicleLogs(data) {
    this.store.dispatch(new LoadVehicleLogsAction(data));
  }

  loadVehicleLogsRequest(
    vehicleId?: number,
    fromdate?: string,
    todate?: string
  ): Observable<Trip[]> {
    return this.httpClient
      .get("GetLogs", {
        params: {
          ...(vehicleId ? { vehicleId: vehicleId.toString() } : {}),
          ...(fromdate ? { fromdate } : {}),
          ...(todate ? { todate } : {}),
          take: "1000",
        },
      })
      .pipe(
        map((trips: Trip[]) => {
          return trips?.map((trip) => {
            const items = trip.items.map((log) => new LogModel(log));
            const tripInstance = new Trip(trip);
            return {
              ...tripInstance,
              items,
            };
          });
        })
      );
  }

  generateReport(data: GenerateReportRequest) {
    this.store.dispatch(new GenerateReportAction(data));
  }

  getCarIconSvg(log: LogModel) {
    const partBeforeArrow = `<svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0)"><circle cx="30.0013" cy="30.0012" r="20.506" fill="#071E36"/><path d="M24.3621 26.6178H22.8583C22.6508 26.6178 22.4824 26.7862 22.4824 26.9937C22.4824 27.2012 22.6508 27.3697 22.8583 27.3697H24.3621C24.5696 27.3697 24.7381 27.2012 24.7381 26.9937C24.7381 26.7862 24.5696 26.6178 24.3621 26.6178Z" fill="white"/><path d="M36.375 27.6321C36.3036 27.4072 34.869 24.3674 34.3321 24.0411C33.7735 23.7012 30.4314 23.6102 30.0013 23.6102C29.5712 23.6102 26.2291 23.7012 25.6704 24.0411C25.1336 24.3666 23.699 27.4072 23.6275 27.6321C23.5644 27.8298 23.6749 28.0411 23.8726 28.1042C24.0727 28.1667 24.2824 28.0569 24.3448 27.8591C24.4629 27.4854 25.7666 24.941 26.0531 24.6884C26.3712 24.5365 28.7133 24.3621 30.0013 24.3621C31.2892 24.3621 33.6314 24.5365 33.9231 24.6704C34.2351 24.9411 35.5397 27.4854 35.6577 27.8591C35.7089 28.0193 35.857 28.1215 36.0164 28.1215C36.054 28.1215 36.0923 28.1163 36.1307 28.1042C36.3276 28.0411 36.4382 27.8298 36.375 27.6321Z" fill="white"/><path d="M24.3621 34.1366C24.1862 34.1366 24.0448 34.2599 24.0042 34.4231L21.7305 33.8546V33.7606C21.7305 33.5531 21.5621 33.3847 21.3546 33.3847C21.147 33.3847 20.9786 33.5532 20.9786 33.7607V35.2645C20.9786 35.8863 21.4847 36.3923 22.1065 36.3923H23.6103C24.2321 36.3923 24.7381 35.8863 24.7381 35.2645V34.5126C24.7381 34.305 24.5696 34.1366 24.3621 34.1366Z" fill="white"/><path d="M38.6479 33.3847C38.4404 33.3847 38.272 33.5532 38.272 33.7607V33.8547L35.9983 34.4231C35.9577 34.26 35.8163 34.1366 35.6404 34.1366C35.4328 34.1366 35.2644 34.3051 35.2644 34.5126V35.2645C35.2644 35.8863 35.7704 36.3923 36.3922 36.3923H37.8961C38.5179 36.3923 39.0239 35.8863 39.0239 35.2645V33.7607C39.0239 33.5532 38.8555 33.3847 38.6479 33.3847Z" fill="white"/><path d="M37.1442 26.6178H35.6404C35.4329 26.6178 35.2645 26.7862 35.2645 26.9937C35.2645 27.2012 35.4329 27.3697 35.6404 27.3697H37.1442C37.3517 27.3697 37.5201 27.2012 37.5201 26.9937C37.5201 26.7862 37.3517 26.6178 37.1442 26.6178Z" fill="white"/><path d="M38.9946 30.1644L37.969 30.3696C37.8427 30.3892 35.3178 30.7531 34.678 30.7531C34.5862 30.826 34.5036 31.3087 34.5133 31.7606C36.8051 31.6524 38.5893 31.5088 38.6179 31.5065L39.0232 31.4516C39.0224 30.9531 39.0149 30.5275 38.9946 30.1644Z" fill="white"/><path d="M38.378 28.5118C38.2096 28.4629 38.1216 28.3403 37.9547 28.0907L37.8261 27.9027C37.6013 27.5892 37.1667 27.5757 36.7464 27.5636C36.6201 27.5599 36.4201 27.5539 36.3253 27.5321C36.293 27.4847 36.2494 27.4448 36.1968 27.4163C35.4953 27.0305 32.8374 26.6178 30.0013 26.6178C27.1652 26.6178 24.5072 27.0305 23.8057 27.4163C23.7531 27.4448 23.7095 27.4847 23.6764 27.5328C23.5817 27.5546 23.3817 27.5606 23.2553 27.5644C22.8358 27.5764 22.4012 27.5892 22.1764 27.9027L22.0478 28.0915C21.8809 28.3411 21.7929 28.4637 21.6245 28.5125C21.3298 28.599 21.1681 28.9013 21.0786 29.4509L21.8215 29.6366C21.8335 29.6366 24.2862 30.0013 25.114 30.0013C25.3982 30.0013 25.6486 30.1095 25.8381 30.3141C26.2366 30.7449 26.2554 31.4998 26.2456 31.7938C27.4554 31.8441 28.7614 31.881 30.0013 31.881C31.2426 31.881 32.5494 31.8442 33.7607 31.7938C33.7524 31.4615 33.7712 30.6291 34.1434 30.2359C34.2893 30.0825 34.4735 30.0013 34.678 30.0013C35.1645 30.0013 37.1277 29.7328 37.8427 29.6291L38.9186 29.4133C38.8284 28.8862 38.6668 28.596 38.378 28.5118ZM32.6329 31.1291H27.3697C27.1621 31.1291 26.9937 30.9606 26.9937 30.7531C26.9937 30.5456 27.1621 30.3772 27.3697 30.3772H32.6328C32.8404 30.3772 33.0088 30.5456 33.0088 30.7531C33.0088 30.9606 32.8404 31.1291 32.6329 31.1291Z" fill="white"/><path d="M38.6976 32.2539C38.6321 32.2591 33.9802 32.6328 30.0013 32.6328C26.0223 32.6328 21.3704 32.2591 21.3245 32.2562L20.9982 32.2223C21.0388 33.0381 21.1643 34.2614 21.5538 34.4689C21.9598 34.6848 26.6568 34.8885 30.0012 34.8885C33.3456 34.8885 38.0426 34.6848 38.4479 34.4682C38.8389 34.2599 38.9645 33.0284 39.0043 32.2126L38.6976 32.2539Z" fill="white"/><path d="M25.2854 30.8238C25.2388 30.7735 25.1892 30.7532 25.114 30.7532C24.2268 30.7532 21.7786 30.3885 21.6749 30.3735L21.0049 30.2065C20.9862 30.5629 20.9794 30.9802 20.9786 31.4636L21.3929 31.5065C21.417 31.5088 23.2005 31.6516 25.4922 31.7607C25.4997 31.4892 25.4636 31.0163 25.2854 30.8238Z" fill="white"/>`;
    // pass rotation to option `transform` to rotate arrow
    const arrowPart = `<path d="M59.9281 28.8479L49.7275 40.5009L50.0409 39.9579C53.9077 33.2604 53.6294 24.9468 49.3232 18.5229V18.5229L59.9281 28.8479Z" fill="#071E36"/>`;
    const partAfterArrow = `</g><defs><clipPath id="clip0"><rect width="60" height="60" fill="white"/></clipPath></defs></svg>`;

    // remove the arrow if the car is not in motion
    return log.logCog === 0 && log.lastCommunicationMinutes > 5
      ? `${partBeforeArrow}${partAfterArrow}`
      : `${partBeforeArrow}${arrowPart}${partAfterArrow}`;
  }

  getPolylineOptions(color: string, isRoadMode = false) {
    const startSymbol = {
      path: google.maps.SymbolPath.CIRCLE,
      strokeColor: color,
      fillColor: "#ffffff",
      fillOpacity: 1,
      scale: 8,
      strokeWeight: 3,
      strokeOpacity: 1,
    };

    const endSymbol = {
      path: google.maps.SymbolPath.CIRCLE,
      strokeColor: color,
      fillOpacity: 1,
      scale: 8,
      strokeWeight: 3,
      strokeOpacity: 1,
    };

    const endSymbolFlag = {
      anchor: new google.maps.Point(6, 6),
      path: "M2.1665 8.12492C2.1665 8.12492 2.70817 7.58325 4.33317 7.58325C5.95817 7.58325 7.0415 8.66658 8.6665 8.66658C10.2915 8.66658 10.8332 8.12492 10.8332 8.12492V1.62492C10.8332 1.62492 10.2915 2.16659 8.6665 2.16659C7.0415 2.16659 5.95817 1.08325 4.33317 1.08325C2.70817 1.08325 2.1665 1.62492 2.1665 1.62492V8.12492Z M2.1665 11.9167V8.125",
      strokeColor: "#ffffff",
      fillColor: "#ffffff",
      fillOpacity: 1,
      scale: 1,
    };

    const lineSymbolArrow = {
      anchor: new google.maps.Point(8, 8),
      path: "M9.92071 2.6824L11.6585 11.8536L2.57308 9.29971C4.59193 8.96159 6.17166 8.37778 7.38068 7.3213C8.60086 6.25509 9.39304 4.75323 9.92071 2.6824Z",
      strokeColor: color,
      fillColor: color,
      fillOpacity: 1,
      scale: 1.5,
      strokeWeight: 1,
      strokeOpacity: 1,
      rotation: 220,
    };

    return {
      strokeColor: color,
      strokeOpacity: 0.8,
      strokeWeight: 3,
      clickable: isRoadMode,
      icons: isRoadMode
        ? [
            {
              icon: lineSymbolArrow,
              offset: "40%",
              repeat: "130px",
              scale: 1,
            },
            {
              icon: startSymbol,
              offset: "0%",
            },
            {
              icon: endSymbol,
              offset: "100%",
            },
            {
              icon: endSymbolFlag,
              offset: "100%",
              fixedRotation: true,
            },
          ]
        : [],
    };
  }

  getMarkerOptsForClosestWaypoint(rotation: number): google.maps.MarkerOptions {
    return {
      draggable: false,
      icon: {
        anchor: new google.maps.Point(8, 8),
        path: "M2.57308 9.29971L11.6585 11.8536L9.92071 2.6824C9.39304 4.75323 8.60086 6.25509 7.38068 7.3213C6.17166 8.37778 4.59193 8.96159 2.57308 9.29971Z",
        strokeColor: "#05add7",
        fillColor: "#05add7",
        fillOpacity: 1,
        scale: 3,
        strokeWeight: 1,
        strokeOpacity: 1,
        rotation: rotation,
      },
    };
  }

  getIconForWaypoint(
    log: LogModel,
    tripColor: string,
    rotation: number
  ): google.maps.Symbol | google.maps.Icon {
    switch (true) {
      case log.tripStart:
        return {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: "#FFFFFF", // White fill
          fillOpacity: 1,
          scale: 10, // Size of the outer circle
          strokeColor: tripColor,
          strokeWeight: 5, // Thicker outline
          anchor: new google.maps.Point(0, 0),
          labelOrigin: new google.maps.Point(0, 0),
        };

      case log.tripEnd:
        return {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: tripColor,
          fillOpacity: 1,
          scale: 10, // Size of the outer circle
          strokeColor: "#FFFFFF", // White border
          strokeWeight: 2,
          anchor: new google.maps.Point(0, 0),
          labelOrigin: new google.maps.Point(0, 0),
        };

      // Special case for speeding incidents
      case isSpeedingIncident(log):
        // Create a red dot for speeding incidents
        return {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: "#FF0000", // Red
          fillOpacity: 1,
          scale: 6, // Size of the dot
          strokeColor: "#FFFFFF", // White border
          strokeWeight: 1,
          anchor: new google.maps.Point(0, 0),
        };

      default: {
        const serverTimeOffsetInMinutes = Math.abs(
          moment(log.logServerDateTime).diff(moment(log.logDateTime), "minutes")
        );

        const strokeColor =
          serverTimeOffsetInMinutes < 10 ? "#FFFFFF" : "#000000";

        const hasMovement =
          log.logCog !== 0 && log.lastCommunicationMinutes <= 5;

        if (hasMovement) {
          return {
            anchor: new google.maps.Point(8, 8),
            path: "M2.57308 9.29971L11.6585 11.8536L9.92071 2.6824C9.39304 4.75323 8.60086 6.25509 7.38068 7.3213C6.17166 8.37778 4.59193 8.96159 2.57308 9.29971Z",
            strokeColor,
            fillColor: this.getWaypointColor(log, tripColor),
            fillOpacity: 1,
            scale: 1.5,
            strokeWeight: 1,
            strokeOpacity: 1,
            rotation: rotation,
          };
        } else {
          return {
            anchor: new google.maps.Point(4, 4),
            path: "M 4, 8 a 4,4 0 1,0 8,0 a 4,4 0 1,0 -8,0",
            strokeColor,
            fillColor: this.getWaypointColor(log, tripColor),
            fillOpacity: 1,
            scale: 1,
            strokeWeight: 1,
            strokeOpacity: 1,
          };
        }
      }
    }
  }

  public getWaypointColor(log: LogModel, defaultColor: string): string {
    if (isSpeedingIncident(log)) {
      return "#FF0000"; // Red for speeding
    } else if (isAccelerationIncident(log)) {
      return "#006400"; // Dark green for acceleration
    } else if (isHardBrakingIncident(log)) {
      return "#90EE90"; // Light green for hard braking
    } else if (isOverRPMIncident(log)) {
      return "#FFFF00"; // Yellow for over RPM
    } else if (isIridiumIncident(log)) {
      return "#FFA500"; // Orange for iridium
    } else {
      return defaultColor; // Default trip color
    }
  }

  resetLogState() {
    this.store.dispatch(new ResetLogStateAction());
  }
}
